import { type FormEvent, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { api, getStationSession, setStationSession } from '../api';
import type { StationContext } from './session';
import { useStationContext } from './session';
import { StationShell } from './StationShell';

function deviceLabel(): string {
  const ua = navigator.userAgent;
  if (/iPhone/.test(ua)) return 'iPhone';
  if (/iPad/.test(ua)) return 'iPad';
  const android = /Android[^;]*;\s*([^)]+?)(?:\sBuild|\))/.exec(ua);
  if (android?.[1]) return android[1].trim().slice(0, 60);
  return /Mac/.test(ua) ? 'Mac' : 'Browser';
}

export function StationPairPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [code, setCode] = useState(params.get('code') ?? '');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const existing = useStationContext();

  const pair = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await api<StationContext & { token: string }>('/station/pair', { auth: 'none', body: { code, deviceLabel: deviceLabel() } });
      setStationSession({ token: res.token, stationId: res.station.id, raceId: res.race.id });
      navigate('/station/setup');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <StationShell>
      <form className="phone-body" onSubmit={pair}>
        <div>
          <div className="step-label">Camera station</div>
          <h1>Pair this phone</h1>
        </div>
        {getStationSession() && existing.data && (
          <div className="card stack" style={{ gap: 8 }}>
            <div>
              Already paired as <b>{existing.data.station.label}</b> for {existing.data.race.name}.
            </div>
            <button type="button" className="btn primary" onClick={() => navigate('/station/setup')}>
              Continue
            </button>
          </div>
        )}
        <p className="muted" style={{ margin: 0 }}>
          Enter the six-character code from the race director, or scan their QR code with your camera.
        </p>
        <label className="field">
          Pairing code
          <input
            className="input mono"
            style={{ fontSize: 26, letterSpacing: '0.12em', textTransform: 'uppercase', minHeight: 56 }}
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="K7Q-4MX"
            autoCapitalize="characters"
            autoComplete="one-time-code"
            required
          />
        </label>
        {error && <div className="notice bad">{error}</div>}
        <button className="btn primary big-btn" type="submit" disabled={busy}>
          Pair phone
        </button>
        <p className="small muted" style={{ margin: 0 }}>
          You’ll need: a tripod or something to prop the phone on, a charger or battery pack, and a clear view of the line.
        </p>
        <p className="small muted" style={{ margin: 0 }}>
          Not sure your phone is up to it? <Link to="/station/check">Run the 30-second device check.</Link>
        </p>
      </form>
    </StationShell>
  );
}
